import * as React from 'react';
import {css, cx, getSystemStyles, useSize} from '@packages/utilities/styled';
import {CardView} from './Card.styles';
import {
  type SizeContextSize,
  type HTMLAttributes,
  type SpringTooling,
} from '../__SharedTypes';
import type {ViewProps} from '../View';

export type CardElevation = 0 | 1 | 2 | 3 | 4 | 5;
export type CardProps = {
  /**
   * Contents of the Card.
   */
  children?: React.ReactNode;
  /**
   * A CSS `background` shorthand for setting all background style properties
   * These properties include `background-color`, `background-image`, `background-position`, `background-size`,
   * `background-repeat`, `background-origin`, `background-clip`, `background-attachment`.
   */
  background?: ViewProps['background'];
  /**
   * A CSS `background-color` property for setting the background color of the Card.
   */
  backgroundColor?: ViewProps['backgroundColor'];
  /**
   * Determines how much shadow the Card has (particularly useful for Popovers and Modals).
   */
  elevation?: CardElevation;
  /**
   * Increases the border-radius on the Card when `true`.
   */
  hasSoftCorners?: boolean;
  /**
   * If isScrollable is `true`, overflow-y is set to `auto`, making it scrollable.
   */
  isScrollable?: boolean;
  /**
   * If isSeamless is `true`, then the Card will have no padding.
   */
  isSeamless?: boolean;
  /**
   * Determines the height of the Card.
   */
  height?: ViewProps['height'];
  /**
   * Determines the padding of the Card.
   */
  padding?: ViewProps['padding'];
  /**
   * Determines the width of the Card.
   */
  width?: ViewProps['width'];
  /**
   * Size determines the padding applied to the Card.
   */
  size?: SizeContextSize;
} & HTMLAttributes &
  SpringTooling;

/**
 * # Card
 *
 * The `Card` component is a base-level layout component that can be used to construct more complex components such as Modals and Popovers.
 *
 * ## Usage
 *
 * ```jsx
 * import {Card} from '@packages/systems/spring';
 *
 * const Example = () => {
 *   return (
 *    <div style={{width: 500}}>
        <Card size="medium" elevation={2}>
          This is a test card!
        </Card>
      </div>
 *   );
 * };
 * ```
 * 
 * @component
 */

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      background,
      backgroundColor,
      children,
      className,
      elevation = 0,
      hasSoftCorners = false,
      height,
      isScrollable = false,
      isSeamless = false,
      padding,
      size: sizeProp,
      width,
      ...rest
    }: CardProps,
    ref
  ) => {
    const size = useSize(sizeProp);
    const styleSystemClass = React.useMemo(
      () =>
        getSystemStyles({
          background,
          backgroundColor,
          height,
          padding,
          width,
        }),
      [background, backgroundColor, height, padding, width]
    );

    const newClassName = React.useMemo(
      () => cx(css(styleSystemClass), className),
      [styleSystemClass, className]
    );

    return (
      <CardView
        className={newClassName}
        data-size={size}
        elevation={elevation}
        hasSoftCorners={hasSoftCorners}
        isScrollable={isScrollable}
        isSeamless={isSeamless}
        ref={ref}
        size={size}
        {...rest}
      >
        {children}
      </CardView>
    );
  }
);

Card.displayName = 'Card';
Card.toString = CardView.toString;

export default Card;
